/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.com/docs/use-static-query/
 */

import * as React from "react"
import PropTypes from "prop-types"
import { useStaticQuery, graphql } from "gatsby"
import { getExtraMetaTagNode } from "../utils/helpers"

function Seo({ pathname, seoData, extraMetaData }) {
  const {
    title,
    readingTime,
    author,
    description,
    hasTemplateTitle,
    image,
    imageWidth,
    imageHeight,
  } = seoData

  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            image
            siteUrl
          }
        }
      }
    `
  )

  const metaDescription = description || site.siteMetadata.description
  const defaultTitle = site.siteMetadata?.title
  const imageDefault = site.siteMetadata.image
  const siteUrl = `${site.siteMetadata.siteUrl}${pathname}`
  const siteLocation =
    pathname.split("/")[2] || pathname.split("/")[1] || "default"
  const headTitle = `${title}${hasTemplateTitle ? " | " + defaultTitle : ""}`
  const siteData = {
    default: {
      lang: "en-US",
      childPath: "/",
      siteName:
        "Live Wedding Bands In Boston For Your Special Day | Red Soda Band",
      link: [
        <link
          rel="alternate"
          href={`${siteUrl}`}
          hrefLang="x-default"
          key="alternate-default"
        />,
        <link
          rel="alternate"
          href={`${site.siteMetadata.siteUrl}/us/boston/`}
          hrefLang="en-us"
          key="alternate-en-us"
        />,
        <link
          rel="alternate"
          href={`${site.siteMetadata.siteUrl}/au/sydney/`}
          hrefLang="en-au"
          key="alternate-en-au"
        />,
        <link
          rel="alternate"
          href={`${site.siteMetadata.siteUrl}/ca/toronto/`}
          hrefLang="en-ca"
          key="alternate-en-ca"
        />,
      ],
    },
    quote: {
      lang: "en-US",
      childPath: "/quote/",
      siteName:
        "Live Wedding Bands In Boston For Your Special Day | Red Soda Band",
      link: [
        <link
          rel="alternate"
          href={`${site.siteMetadata.siteUrl}/quote/`}
          hrefLang="en-au"
          key="alternate-en-us-quote"
        />,
        <link
          rel="alternate"
          href={`${site.siteMetadata.siteUrl}/quote/`}
          hrefLang="en-us"
          key="alternate-en-us-quote"
        />,
        <link
          rel="alternate"
          href={`${site.siteMetadata.siteUrl}/quote/`}
          hrefLang="en-ca"
          key="alternate-en-ca-quote"
        />,
      ],
    },
    toronto: {
      childPath: "/ca/toronto/",
      lang: "en-CA",
      siteName: "Wedding Music Bands in Toronto - Red Soda Band",
    },
    sydney: {
      childPath: "/au/sydney/",
      lang: "en-AU",
      siteName: "Wedding Music Bands in Sydney - Red Soda Band",
    },
    boston: {
      childPath: "/us/boston/",
      lang: "en-US",
      siteName:
        "Live Wedding Bands In Boston For Your Special Day | Red Soda Band",
    },
    preview: {
      childPath: "/preview",
      lang: "en-US",
      siteName: "Preview Mode",
    },
  }

  const robots = {
    disallow: [
      <meta name="robots" content="noindex,nofollow" key="robots-disallow" />,
      <meta
        name="googlebot"
        content="noindex,nofollow"
        key="googlebot-disallow"
      />,
    ],
    allow: (
      <meta
        name="robots"
        content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        key="robots-allows"
      />
    ),
  }
  const siteLocationChildPath = `${site?.siteMetadata?.siteUrl}${siteData?.[siteLocation]?.childPath}`

  return (
    <>
      <html lang={siteData[siteLocation]?.lang} />
      <title>{headTitle}</title>
      {siteData[siteLocation]?.link}
      {getExtraMetaTagNode(extraMetaData)}
      <link rel="profile" href="http://gmpg.org/xfn/11" />
      <script type="application/ld+json">
        {`{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebSite",
              "@id": "${siteUrl}#website",
              "url": "${siteUrl}",
              "name": "${headTitle}",
              "description": "${metaDescription}",
              "inLanguage": "${siteData[siteLocation].lang}"
            },
            {
              "@type": "ImageObject",
              "inLanguage": "${siteData[siteLocation].lang}",
              "@id": "${siteUrl}#primaryimage",
              "url": "${image ? `${image}` : `${imageDefault}`}",
              "contentUrl": "${image ? `${image}` : `${imageDefault}`}",
              "width": ${imageWidth || 1200},
              "height": ${imageHeight || 630}
            },
            {
              "@type": "WebPage",
              "@id": "${siteUrl}#webpage",
              "url": "${siteUrl}",
              "name": "${headTitle}",
              "isPartOf": { "@id": "${siteUrl}#website" },
              "datePublished": "2022-05-17T02:20:24+00:00",
              "dateModified": "2022-05-17T21:53:54+00:00",
              "description": "${metaDescription}",
              "breadcrumb": { "@id": "${siteUrl}#breadcrumb" },
              "inLanguage": "${siteData[siteLocation].lang}",
              "potentialAction": [
                { "@type": "ReadAction", "target": ["${siteUrl}"] }
              ]
            },
            {
              "@type": "BreadcrumbList",
              "@id": "${siteUrl}#breadcrumb",
              "itemListElement": [
                { 
                  "@type": "ListItem", 
                  "position": 1, 
                  "name": "Home"${
                    siteLocation !== "default"
                      ? `, "item":"${siteLocationChildPath}"`
                      : ""
                  } }
                  ${
                    pathname.split("/") && pathname.split("/")[3]
                      ? `,{ 
                        "@type": "ListItem", "position": 2, 
                        "name": "${title}", 
                        "item":"${siteUrl}"
                      }`
                      : ""
                  }
              ]
            }
            ${
              author
                ? `,{ 
                "@type": "Person",
                "@id":"${siteLocationChildPath}#/schema/person/d6329adcaf6fb7ce64eb9d95f6d0e30b",
                "name":"${author}",
                "sameAs":["${siteLocationChildPath}"] 
              }`
                : ""
            }
          ]
        }`}
      </script>
      <link rel="canonical" href={siteUrl} />
      <link
        rel="shortcut icon"
        type="image/x-icon"
        href={`${site.siteMetadata.siteUrl}/static/images/favicon.png`}
      />
      <link
        rel="apple-touch-icon"
        href={`${site.siteMetadata.siteUrl}/static/images/favicon.png`}
      ></link>
      {process.env.GATSBY_DISABLE_ROBOT ? robots.disallow : robots.allow}
      <meta
        name="google-site-verification"
        content={process.env.GATSBY_GG_SEARCH_CONSOLE}
      />
      <meta name="description" content={`${metaDescription}`} />
      <meta property="og:locale" content={`en_US`} />
      <meta property="og:type" content={`website`} />
      <meta property="og:title" content={`${title}`} />
      <meta property="og:description" content={`${metaDescription}`} />
      <meta property="og:url" content={`${siteUrl}`} />
      <meta
        property="og:site_name"
        content={`${siteData[siteLocation].siteName}`}
      />
      <meta
        property="og:image"
        content={`${image ? `${image}` : `${imageDefault}`}`}
      />
      <meta
        property="og:image:type"
        content={`image/${
          image?.split(".")[image?.split(".").length - 1] || "png"
        }`}
      />
      <meta property="og:image:width" content={imageWidth || 1200} />
      <meta property="og:image:height" content={imageHeight || 630} />
      <meta name="twitter:site" content={`@redsodaband`} />
      <meta name="twitter:creator" content="@redsodaband" />
      <meta name="twitter:title" content={`${title}`} />
      <meta name="twitter:card" content={`summary`} />
      <meta name="twitter:description" content={`${metaDescription}`} />
      {readingTime && (
        <meta name="twitter:label1" content="Est. reading time" />
      )}
      {readingTime && <meta name="twitter:data1" content={readingTime} />}
      {author && <meta name="twitter:label2" content="Written by" />}
      {author && <meta name="twitter:data2" content={author} />}

      <meta
        property="article:publisher"
        content="https://www.facebook.com/RedSodaBand"
      />
      <meta
        property="article:modified_time"
        content="2022-03-17T10:28:28+00:00"
      />
    </>
  )
}

Seo.propTypes = {
  pathname: PropTypes.string.isRequired,
  seoData: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.string,
    readingTime: PropTypes.string,
    author: PropTypes.string,
    hasTemplateTitle: PropTypes.bool,
    imageWidth: PropTypes.number,
    imageHeight: PropTypes.number,
  }).isRequired,
}

export default Seo
